import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "fleet-tooling",
  kind: "horizontal",
  title: "Fleet Deployment & Update Tooling",
  shortTitle: "Fleet Tooling",
  status: "draft",
  tier: "experience",
  seoTitle: "Fleet Deployment & Update Tooling | General Applications",
  seoDescription:
    "Installers, scripts, interfaces, packaging, and controlled update tooling for distributed units on connected or restricted networks.",
  summary:
    "Installers, scripts, configuration tools, and operator interfaces that keep distributed units in a known state, change them deliberately, and make recovery possible when a rollout does not go to plan.",
  image: "/images/blog/shipping-s1000d-offline-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: [
    "military",
    "enterprise",
    "energy",
    "media-entertainment",
  ],
  relatedPostSlugs: [
    "shipping-s1000d-offline",
    "replicating-without-a-database",
  ],
  engineering: [
    "Self-contained Windows and Linux installers for restricted or disconnected environments",
    "Package, script, and configuration tooling for distributed software and hardware units",
    "Operator-facing administration interfaces for installation, configuration, and support",
    "Version, compatibility, rollout, rollback, and recovery workflows",
    "Offline distribution packages, dependency handling, and verification steps",
    "Fleet inventory, configuration-state, and update-status visibility",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "A Fleet Is More Than a Device Count",
    },
    {
      type: "paragraph",
      text: "A fleet may be field equipment, workstations, consoles, servers, or units spread across facilities. The difficult part is not producing an installer once. It is knowing what version, configuration, dependency set, and recovery path apply to each unit when networks are limited, time is short, and a failed update cannot be treated as a local inconvenience.",
    },
    {
      type: "heading",
      level: 2,
      text: "Make Change Operable",
    },
    {
      type: "paragraph",
      text: "Useful fleet tooling gives operators a deliberate path to install, configure, verify, update, and recover. Scripts, packages, and graphical interfaces all have a place, but they need the same source of truth for compatible versions, prerequisites, configuration, evidence, and the next action when a unit does not conform.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the unit types, software and configuration states, network constraints, operating windows, and recovery obligations before choosing a deployment approach.",
      produces: [
        "Fleet, unit-class, and configuration-state inventory",
        "Installation, update, and recovery workflow map",
        "Connectivity, security, and offline-distribution constraint analysis",
        "Operator, maintainer, and approval responsibility model",
      ],
      fit: {
        good: [
          "Distributed units that must be installed, configured, or updated consistently across facilities or restricted networks",
          "Teams replacing ad hoc scripts and undocumented operator knowledge with a controlled lifecycle",
        ],
        poor: [
          "A consumer-style automatic update with no operating, security, or recovery constraint",
          "A fleet whose unit ownership, supported configurations, and intended software baseline are not yet defined",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the package, configuration, state, and recovery model that makes change repeatable across different unit classes.",
      produces: [
        "Installer, script, service, and interface architecture",
        "Version, compatibility, and configuration-state model",
        "Distribution, verification, rollback, and recovery design",
        "Operator and maintainer workflow specification",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the packaging, preflight checks, execution steps, evidence capture, and exception handling required for safe operation.",
      produces: [
        "Package and dependency definitions",
        "Installation, configuration, update, and rollback procedures",
        "Preflight, post-installation, and health-verification checks",
        "Exception, recovery, and support runbooks",
      ],
    },
    implementation: {
      focus:
        "Build and validate the tooling against representative units and the real operating conditions that govern deployment.",
      produces: [
        "Controlled installers, scripts, services, or administration interfaces",
        "Repeatable unit configuration and update workflows",
        "Verified recovery path and deployment evidence",
        "Operator-facing documentation and support materials",
      ],
    },
  },
};
